import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, KeyRound, Loader2, Plug, Trash2, XCircle } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { useToast } from '../../../context/ToastContext'
import {
  CollapsibleSection, ConfirmDialog, PanelState, errorMessage, inputCls, primaryBtn, secondaryBtn,
} from './shared'

const SECRET_FIELDS = [
  { key: 'consumer_key', label: 'Consumer key', has: 'has_consumer_key' },
  { key: 'consumer_secret', label: 'Consumer secret', has: 'has_consumer_secret' },
  { key: 'token_id', label: 'Token ID', has: 'has_token_id' },
  { key: 'token_secret', label: 'Token secret', has: 'has_token_secret' },
]

const EMPTY_SECRETS = { consumer_key: '', consumer_secret: '', token_id: '', token_secret: '' }

// Which NetSuite account this OpCo posts to. Secrets are write-only: the server
// only says which are saved, so a saved secret shows as a placeholder and is kept
// unless a new value is typed.
export default function NetSuitePanel({ opco }) {
  const toast = useToast()
  const [connection, setConnection] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [useEnv, setUseEnv] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [secrets, setSecrets] = useState(EMPTY_SECRETS)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [removing, setRemoving] = useState(false)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [open, setOpen] = useState(false)

  const apply = useCallback((conn) => {
    setConnection(conn)
    setUseEnv(Boolean(conn?.use_env))
    setAccountId(conn?.account_id || '')
    setSecrets(EMPTY_SECRETS)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await opcoApi.getNsConnection(opco.id)
      apply(res.data?.connection || null)
    } catch (err) {
      setError(errorMessage(err, 'Could not load the NetSuite connection'))
    } finally {
      setLoading(false)
    }
  }, [opco.id, apply])

  useEffect(() => { load() }, [load])

  const missing = useEnv ? [] : [
    !accountId.trim() && 'account ID',
    ...SECRET_FIELDS.filter((f) => !secrets[f.key].trim() && !connection?.[f.has]).map((f) => f.label.toLowerCase()),
  ].filter(Boolean)

  const save = async (e) => {
    e.preventDefault()
    if (missing.length) return
    setSaving(true)
    setTestResult(null)
    try {
      const body = { use_env: useEnv }
      if (!useEnv) {
        body.account_id = accountId.trim()
        // Blank secrets are left out: the server keeps the saved one.
        SECRET_FIELDS.forEach((f) => { if (secrets[f.key].trim()) body[f.key] = secrets[f.key].trim() })
      }
      const res = await opcoApi.saveNsConnection(opco.id, body)
      apply(res.data?.connection || null)
      toast.success('NetSuite connection saved', `${opco.name} · use Test connection to check it`)
    } catch (err) {
      toast.error('Could not save the connection', errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const test = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await opcoApi.testNsConnection(opco.id)
      setTestResult(res.data)
      await load()
    } catch (err) {
      setTestResult({ connected: false, error: errorMessage(err) })
    } finally {
      setTesting(false)
    }
  }

  const confirmRemove = async () => {
    setRemoveBusy(true)
    try {
      await opcoApi.deleteNsConnection(opco.id)
      toast.success('Connection removed', `${opco.name} no longer has NetSuite access`)
      setRemoving(false)
      setTestResult(null)
      apply(null)
    } catch (err) {
      toast.error('Could not remove the connection', errorMessage(err))
    } finally {
      setRemoveBusy(false)
    }
  }

  let status = 'Not configured. This OpCo cannot use NetSuite until a connection is added.'
  if (connection?.use_env) status = 'Uses the deployment’s NetSuite account (shared with other OpCos set the same way).'
  else if (connection) status = `Own NetSuite account ${connection.account_id || ''}`.trim()

  // What the collapsed header says: the state at a glance, no need to open it.
  const lastTest = connection?.last_tested_at ? (connection.last_test_ok ? 'Connected' : 'Last test failed') : null
  let summary = 'Not set up'
  let tone = 'muted'
  if (loading) summary = 'Loading...'
  else if (error) { summary = 'Could not load'; tone = 'bad' }
  else if (connection?.use_env) summary = 'Uses the deployment’s account'
  else if (connection) summary = `Own account ${connection.account_id || ''}`.trim()
  if (!loading && !error && lastTest) {
    summary += ` · ${lastTest}`
    tone = connection.last_test_ok ? 'ok' : 'bad'
  }

  return (
    <CollapsibleSection
      id={`ns-body-${opco.id}`}
      icon={Plug}
      title="NetSuite"
      ariaLabel="NetSuite connection"
      summary={summary}
      tone={tone}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      <p className="px-5 pt-4 text-xs text-gray-500 dark:text-gray-400">
        The NetSuite account this OpCo posts to. Its items, vendors, customers and mappings are kept separate from every other OpCo&rsquo;s.
      </p>

      <PanelState loading={loading} error={error} onRetry={load}>
        <form onSubmit={save} className="space-y-4 px-5 py-4" noValidate>
          <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="ns-status">{status}</p>

          <fieldset className="space-y-2">
            <legend className="sr-only">Account</legend>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input type="radio" name={`ns-mode-${opco.id}`} checked={!useEnv} onChange={() => setUseEnv(false)} />
              This OpCo has its own NetSuite account
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input type="radio" name={`ns-mode-${opco.id}`} checked={useEnv} onChange={() => setUseEnv(true)} />
              Use the deployment&rsquo;s NetSuite account
            </label>
          </fieldset>

          {!useEnv && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400" htmlFor={`ns-account-${opco.id}`}>
                  Account ID
                </label>
                <input
                  id={`ns-account-${opco.id}`}
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  placeholder="e.g. 1234567 or 1234567_SB1"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              {SECRET_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400" htmlFor={`ns-${f.key}-${opco.id}`}>
                    {f.label}
                  </label>
                  <input
                    id={`ns-${f.key}-${opco.id}`}
                    type="password"
                    value={secrets[f.key]}
                    onChange={(e) => setSecrets((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={connection?.[f.has] ? 'Saved — leave blank to keep' : ''}
                    className={inputCls}
                    autoComplete="new-password"
                  />
                </div>
              ))}
            </div>
          )}

          {!useEnv && missing.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Still needed: {missing.join(', ')}.</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={saving || missing.length > 0 || opco.is_active === false} className={primaryBtn}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Save connection
            </button>
            <button type="button" onClick={test} disabled={testing || !connection} className={secondaryBtn}>
              {testing && <Loader2 className="h-4 w-4 animate-spin" />}
              Test connection
            </button>
            {connection && (
              <button type="button" onClick={() => setRemoving(true)} className={`${secondaryBtn} text-red-600 dark:text-red-400`}>
                <Trash2 className="h-4 w-4" /> Remove
              </button>
            )}
          </div>

          {(testResult || connection?.last_tested_at) && (() => {
            const ok = testResult ? Boolean(testResult.connected) : Boolean(connection?.last_test_ok)
            const message = testResult
              ? (testResult.connected ? `Connected to ${testResult.account_id || 'NetSuite'}` : testResult.error)
              : connection?.last_test_message
            return (
              <p
                role="status"
                className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
              >
                {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {message || (ok ? 'Connected' : 'Connection failed')}
              </p>
            )
          })()}
        </form>
      </PanelState>

      {removing && (
        <ConfirmDialog
          title="Remove NetSuite connection"
          message={`${opco.name} will no longer be able to use NetSuite until a connection is added again. Cached NetSuite data for it is kept.`}
          confirmLabel="Remove"
          danger
          busy={removeBusy}
          onConfirm={confirmRemove}
          onCancel={() => setRemoving(false)}
        />
      )}
    </CollapsibleSection>
  )
}
